import React, { useState, useEffect } from 'react';
import { X, Loader2, Receipt } from 'lucide-react';
import { conciergeAPI, ConciergeExpense, ConciergeReservation } from '../../api/concierge';
import { Button } from '../ui/Button';

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  propertyId: number;
  year: number;
  month: number;
  expense?: ConciergeExpense | null;
}

export const ExpenseModal: React.FC<ExpenseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  propertyId,
  year,
  month,
  expense,
}) => {
  const [formData, setFormData] = useState({
    expense_date: '',
    description: '',
    amount: '',
    reservation_id: '',
  });

  const [reservations, setReservations] = useState<ConciergeReservation[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingReservations, setIsLoadingReservations] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadReservations();
    }
  }, [isOpen, propertyId, year, month]);

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (expense) {
      setFormData({
        expense_date: formatDateForInput(expense.expense_date),
        description: expense.description,
        amount: expense.amount.toString(),
        reservation_id: expense.reservation_id ? expense.reservation_id.toString() : '',
      });
    } else {
      const currentDate = new Date();
      const dateString = currentDate.toISOString().split('T')[0];
      setFormData({
        expense_date: dateString,
        description: '',
        amount: '',
        reservation_id: '',
      });
    }
    setError(null);
  }, [expense, isOpen]);

  const loadReservations = async () => {
    setIsLoadingReservations(true);
    try {
      const data = await conciergeAPI.getReservations(propertyId, year, month);
      setReservations(data);
    } catch (err) {
      console.error('Error loading reservations:', err);
    } finally {
      setIsLoadingReservations(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.expense_date) {
      setError('La date est requise');
      return;
    }

    if (!formData.description.trim()) {
      setError('La description est requise');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Le montant doit être un nombre positif');
      return;
    }

    setIsSubmitting(true);

    try {
      const expenseData: ConciergeExpense = {
        property_id: propertyId,
        expense_date: formData.expense_date,
        description: formData.description.trim(),
        amount: amount,
        reservation_id: formData.reservation_id ? parseInt(formData.reservation_id) : undefined,
      };

      if (expense?.id) {
        await conciergeAPI.updateExpense(expense.id, expenseData);
      } else {
        await conciergeAPI.createExpense(expenseData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving expense:', err);
      setError('Erreur lors de la sauvegarde de la dépense');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-hidden">
      <div className="relative w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white dark:bg-gray-800 p-4 sm:p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 sm:right-4 sm:top-4 h-9 w-9 sm:h-auto sm:w-auto flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-manipulation"
          disabled={isSubmitting}
        >
          <X className="h-5 w-5 sm:h-5 sm:w-5" />
        </button>

        <div className="mb-4 sm:mb-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
            <div className="p-1.5 sm:p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              {expense ? 'Modifier la dépense' : 'Nouvelle dépense'}
            </h2>
          </div>
        </div>

        {error && (
          <div className="mb-3 sm:mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 sm:p-3">
            <p className="text-red-800 dark:text-red-200 text-xs sm:text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 w-full">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date de la dépense *
              </label>
              <input
                type="date"
                name="expense_date"
                value={formData.expense_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Montant (DA) *
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white text-sm"
              required
              disabled={isSubmitting}
              placeholder="Ex: Changement d'ampoule, réparation de prise, ménage..."
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lier à une réservation (optionnel)
            </label>
            {isLoadingReservations ? (
              <div className="flex items-center justify-center py-3 border border-gray-300 dark:border-gray-600 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <select
                name="reservation_id"
                value={formData.reservation_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                disabled={isSubmitting}
              >
                <option value="">Aucune réservation</option>
                {reservations.map((reservation) => {
                  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
                  const label = isMobile
                    ? reservation.guest_name
                    : `${reservation.guest_name} (${new Date(reservation.start_date).toLocaleDateString('fr-FR')} - ${new Date(reservation.end_date).toLocaleDateString('fr-FR')})`;

                  return (
                    <option key={reservation.id} value={reservation.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            )}
            <p className="mt-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
              Vous pouvez lier cette dépense à une réservation spécifique (ex: ménage après le départ d'un client)
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="!bg-orange-600 hover:!bg-orange-700 w-full sm:w-auto"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};